import { Info, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import type { SowCreditPoolType } from '@wevo/wevo-access/sow-credit-pools';

import { cn } from '../../../lib/utils';
import {
    formFieldGroupControlClassName,
    formFieldGroupSurfaceClassName,
} from '../../ui/formPrimitives';
import { Input } from '../../ui/input';
import { Select } from '../../ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { overlineClassName } from '../../ui/typography';
import { Field } from './creditDialogPrimitives';
import {
    getSelectableCreditPoolOptions,
    type AllocationAvailabilityMode,
    type AllocationCadence,
    type AllocationRow,
    type AllocationUnusedBehavior,
} from './sowAllocationModel';

type SelectOption = { value: string; label: ReactNode };

export function SowAllocationRowsEditor({
    unitMode,
    rows,
    labelPrefix,
    allowRemoveSingleRow = false,
    getSelectableOptions = (_, currentPoolType) =>
        getSelectableCreditPoolOptions([currentPoolType]),
    onRemoveRow,
    onUpdateRow,
}: {
    unitMode: 'credit' | 'token';
    rows: AllocationRow[];
    labelPrefix: string;
    allowRemoveSingleRow?: boolean;
    getSelectableOptions?: (rowKey: string, currentPoolType: SowCreditPoolType) => SelectOption[];
    onRemoveRow: (key: string) => void;
    onUpdateRow: (key: string, patch: Partial<AllocationRow>) => void;
}) {
    return (
        <div className={cn(formFieldGroupSurfaceClassName, 'space-y-6')}>
            {rows.map((row, index) => (
                <div
                    key={row.key}
                    className={cn(
                        'space-y-3',
                        index !== 0 && 'border-t border-neutral-300 pt-6 dark:border-neutral-600',
                    )}
                >
                    <div className="flex items-center justify-between gap-2">
                        <span className={overlineClassName}>
                            {labelPrefix} {index + 1}
                        </span>
                        {rows.length > 1 || allowRemoveSingleRow ? (
                            <button
                                type="button"
                                className="rounded p-1 text-neutral-600 hover:bg-white/90 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-700/80 dark:hover:text-neutral-100"
                                onClick={() => onRemoveRow(row.key)}
                                aria-label={`Remove ${labelPrefix.toLowerCase()} ${index + 1}`}
                            >
                                <Trash2 className="size-4" />
                            </button>
                        ) : null}
                    </div>
                    <div
                        className={
                            unitMode === 'credit' ? 'grid gap-3 md:grid-cols-2' : 'grid gap-3'
                        }
                    >
                        {unitMode === 'credit' ? (
                            <Field label="Credit type" required>
                                <Select
                                    ariaLabel={`Credit type for ${labelPrefix.toLowerCase()} ${index + 1}`}
                                    className={formFieldGroupControlClassName}
                                    options={getSelectableOptions(row.key, row.poolType)}
                                    value={row.poolType}
                                    onValueChange={(nextValue) =>
                                        onUpdateRow(row.key, {
                                            poolType: nextValue as SowCreditPoolType,
                                        })
                                    }
                                />
                            </Field>
                        ) : null}
                        <Field
                            label={unitMode === 'token' ? 'Initial tokens' : 'Initial amount'}
                            required
                        >
                            <Input
                                variant="outlined"
                                inputMode="decimal"
                                unit={unitMode === 'token' ? 'tokens' : 'credits'}
                                aria-label={
                                    unitMode === 'token'
                                        ? `Initial tokens for ${labelPrefix.toLowerCase()} ${index + 1}`
                                        : `Initial amount for ${labelPrefix.toLowerCase()} ${index + 1}`
                                }
                                value={row.initialAmount}
                                onChange={(event) =>
                                    onUpdateRow(row.key, { initialAmount: event.target.value })
                                }
                            />
                        </Field>
                    </div>
                    {unitMode === 'credit' ? (
                        <div className="grid gap-3 md:grid-cols-2">
                            <Field label="Availability mode" required>
                                <Select
                                    ariaLabel={`Availability mode for ${labelPrefix.toLowerCase()} ${index + 1}`}
                                    className={formFieldGroupControlClassName}
                                    options={[
                                        { value: 'all_upfront', label: 'All upfront' },
                                        { value: 'periodic_release', label: 'Periodic release' },
                                    ]}
                                    value={row.availabilityMode}
                                    onValueChange={(nextValue) =>
                                        onUpdateRow(row.key, {
                                            availabilityMode:
                                                nextValue as AllocationAvailabilityMode,
                                        })
                                    }
                                />
                            </Field>
                            {row.availabilityMode === 'periodic_release' ? (
                                <Field label="Cadence" required>
                                    <Select
                                        ariaLabel={`Cadence for ${labelPrefix.toLowerCase()} ${index + 1}`}
                                        className={formFieldGroupControlClassName}
                                        options={[
                                            { value: 'monthly', label: 'Monthly' },
                                            { value: 'quarterly', label: 'Quarterly' },
                                            { value: 'yearly', label: 'Yearly' },
                                        ]}
                                        value={row.cadence}
                                        onValueChange={(nextValue) =>
                                            onUpdateRow(row.key, {
                                                cadence: nextValue as AllocationCadence,
                                            })
                                        }
                                    />
                                </Field>
                            ) : (
                                <div />
                            )}
                        </div>
                    ) : null}
                    {unitMode === 'credit' && row.availabilityMode === 'periodic_release' ? (
                        <div className="grid gap-3 md:grid-cols-3">
                            <Field label="Release amount" required>
                                <Input
                                    variant="outlined"
                                    inputMode="decimal"
                                    unit="credits"
                                    aria-label={`Release amount for ${labelPrefix.toLowerCase()} ${index + 1}`}
                                    value={row.releaseAmount}
                                    onChange={(event) =>
                                        onUpdateRow(row.key, { releaseAmount: event.target.value })
                                    }
                                />
                            </Field>
                            <Field label="Anchor day" required>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <Input
                                            variant="outlined"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            className="text-right tabular-nums"
                                            aria-label={`Anchor day for ${labelPrefix.toLowerCase()} ${index + 1}`}
                                            value={row.anchorDay}
                                            onChange={(event) =>
                                                onUpdateRow(row.key, {
                                                    anchorDay: event.target.value,
                                                    anchorDayManuallyEdited: true,
                                                })
                                            }
                                        />
                                        <TooltipProvider delayDuration={0}>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <button
                                                        type="button"
                                                        className="inline-flex items-center justify-center rounded-full p-1 text-primary-500 hover:bg-primary-500/10"
                                                        aria-label={`Anchor day help for ${labelPrefix.toLowerCase()} ${index + 1}`}
                                                    >
                                                        <Info
                                                            className="size-4 shrink-0"
                                                            aria-hidden
                                                        />
                                                    </button>
                                                </TooltipTrigger>
                                                <TooltipContent className="max-w-xs">
                                                    <p>
                                                        Anchor day is the day each release period
                                                        starts. Unused credits from the prior period
                                                        remain valid through 11:59:59 PM local time
                                                        on the day before the anchor day, and the
                                                        new period begins at 12:00 AM local time on
                                                        the anchor day. Usually this should match
                                                        the SOW start day.
                                                    </p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                    <p className="text-xs text-neutral-600">
                                        Defaults to the SOW start day-of-month until you override
                                        it.
                                    </p>
                                </div>
                            </Field>
                            <Field label="Unused credits" required>
                                <Select
                                    ariaLabel={`Unused credits for ${labelPrefix.toLowerCase()} ${index + 1}`}
                                    className={formFieldGroupControlClassName}
                                    options={[
                                        { value: 'carry_forward', label: 'Carry forward' },
                                        {
                                            value: 'expire_end_of_period',
                                            label: 'Expire at period end',
                                        },
                                    ]}
                                    value={row.unusedBehavior}
                                    onValueChange={(nextValue) =>
                                        onUpdateRow(row.key, {
                                            unusedBehavior: nextValue as AllocationUnusedBehavior,
                                        })
                                    }
                                />
                            </Field>
                        </div>
                    ) : null}
                </div>
            ))}
        </div>
    );
}
